
import React, { useEffect, useMemo, useState, useCallback } from "react";
import InstituteSidebar from "./Institutesidebar";
import InstituteTopbar from "./Institutetopbar";
import Pager from "./Pager";
import useInstituteData, { fmtDate, downloadCsv } from "../Useinstitutedata";
import {
  getInstituteAvailableExams,
} from "../api/instituteclient";
import { djangoApi } from "../api/auth";

const PAGE_SIZE = 10;

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .replaceAll("_", " ")
    .toLowerCase();

const statusLabel = (registration, attempted) => {
  if (attempted) return "Attempted";

  const status = normalizeStatus(registration.status);
  if (!status) return "Pending";

  const labels = {
    "payment pending": "Payment Pending",
    "verification pending": "Verification Pending",
    "in progress": "In Progress",
    "auto submitted": "Auto Submitted",
    registered: "Registered",
    paid: "Paid",
    scheduled: "Scheduled",
    ready: "Ready",
    submitted: "Submitted",
    terminated: "Terminated",
  };

  return labels[status] || status.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const statusClass = (label) => {
  if (["Attempted", "Submitted", "Auto Submitted", "Paid", "Ready"].includes(label)) {
    return "active";
  }
  if (["Terminated", "Payment Pending", "Verification Pending"].includes(label)) {
    return "inactive";
  }
  return "pending";
};

const responseArray = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export default function InstituteExams() {
  const {
    students,
    purchases,
    scores,
    loading,
    error,
    reload,
  } = useInstituteData();

  const [availableExams, setAvailableExams] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(true);
  const [availableError, setAvailableError] = useState("");
  const [selectedExamId, setSelectedExamId] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [registering, setRegistering] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState("");
  const [registrationError, setRegistrationError] = useState("");

  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("All");
  const [page, setPage] = useState(1);

  const loadAvailableExams = useCallback(async () => {
    setAvailableLoading(true);
    setAvailableError("");

    try {
      const response = await getInstituteAvailableExams();
      setAvailableExams(responseArray(response));
    } catch (err) {
      setAvailableError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to load available exams."
      );
    } finally {
      setAvailableLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableExams();
  }, [loadAvailableExams]);

  const studentOptions = useMemo(
    () =>
      students.filter(
        (student) =>
          student.isActive !== false &&
          student.is_active !== false &&
          (student.userId ?? student.user_id) != null
      ),
    [students]
  );

  const rows = useMemo(() => {
    const studentByUserId = new Map(
      students.map((student) => [
        String(student.userId ?? student.user_id ?? student.id),
        student,
      ])
    );

    return purchases
      .map((registration) => {
        const userId = registration.userId ?? registration.user_id ?? registration.student_id;
        const student = studentByUserId.get(String(userId));
        const studentName =
          registration.studentName ||
          registration.student_name ||
          student?.fullName ||
          student?.full_name ||
          student?.name ||
          "—";

        const registrationId = registration.id;
        const examId = registration.examId ?? registration.exam_id;
        const examName =
          registration.examName ||
          registration.exam_name ||
          registration.courseName ||
          registration.course_name ||
          "—";

        const courseName =
          registration.courseName ||
          registration.course_name ||
          "—";

        const attempted = scores.some((score) => {
          const scoreUserId = score.userId ?? score.user_id;
          const scoreRegistrationId = score.registrationId ?? score.registration_id;
          const scoreExamId = score.examId ?? score.exam_id;
          const scoreCourseName =
            score.courseName || score.course_name || score.examName || "";

          if (
            scoreRegistrationId != null &&
            registrationId != null &&
            String(scoreRegistrationId) === String(registrationId)
          ) {
            return true;
          }

          if (
            scoreExamId != null &&
            examId != null &&
            String(scoreExamId) === String(examId) &&
            String(scoreUserId) === String(userId)
          ) {
            return true;
          }

          return (
            scoreUserId != null &&
            userId != null &&
            String(scoreUserId) === String(userId) &&
            scoreCourseName.toLowerCase() === courseName.toLowerCase()
          );
        });

        return {
          ...registration,
          id: registration.id,
          userId,
          studentName,
          email:
            registration.studentEmail ||
            registration.student_email ||
            registration.email ||
            student?.email ||
            "",
          examId,
          examName,
          courseName:
            courseName === "—"
              ? registration.examName || registration.exam_name || "—"
              : courseName,
          registeredAt:
            registration.registeredAt ||
            registration.registered_at ||
            null,
          fee: registration.fee,
          attempted,
          displayStatus: statusLabel(registration, attempted),
        };
      })
      .sort((a, b) =>
        String(b.registeredAt || "").localeCompare(String(a.registeredAt || ""))
      );
  }, [purchases, scores, students]);

  const courseNames = useMemo(
    () =>
      [...new Set(rows.map((row) => row.courseName).filter((name) => name && name !== "—"))],
    [rows]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !query ||
        row.studentName.toLowerCase().includes(query) ||
        String(row.email || "").toLowerCase().includes(query) ||
        row.courseName.toLowerCase().includes(query) ||
        row.examName.toLowerCase().includes(query);

      return (
        matchesSearch &&
        (courseFilter === "All" || row.courseName === courseFilter)
      );
    });
  }, [rows, search, courseFilter]);

  useEffect(() => setPage(1), [search, courseFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const cards = [
    { label: "Exams Booked", value: filtered.length, icon: "bi-calendar-check" },
    {
      label: "Attempted",
      value: filtered.filter((row) => row.attempted).length,
      icon: "bi-journal-check",
    },
    {
      label: "Not Attempted",
      value: filtered.filter((row) => !row.attempted).length,
      icon: "bi-hourglass-split",
    },
  ];

  const selectedExam = availableExams.find(
    (exam) => String(exam.id) === String(selectedExamId)
  );

  const exportCsv = () =>
    downloadCsv("institute-exams.csv", [
      [
        "Student",
        "Email",
        "Exam",
        "Course",
        "Registered Date",
        "Fee",
        "Registration Status",
        "Attempted",
      ],
      ...filtered.map((row) => [
        row.studentName,
        row.email || "",
        row.examName,
        row.courseName,
        row.registeredAt || "",
        row.fee ?? "",
        row.displayStatus,
        row.attempted ? "Yes" : "No",
      ]),
    ]);

  const registerStudent = async (event) => {
  event.preventDefault();
  setRegistrationMessage("");
  setRegistrationError("");

  if (selectedStudentIds.length === 0 || !selectedExamId) {
    setRegistrationError("Please select at least one student and an exam.");
    return;
  }

  setRegistering(true);

  let successful = 0;
  let failed = 0;
  const failures = [];

  try {
    for (const studentId of selectedStudentIds) {
      try {
        await djangoApi.post("/api/auth/institute/registrations/", {
          student_id: Number(studentId),
          exam_id: Number(selectedExamId),
        });

        successful++;
      } catch (err) {
        failed++;

        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Registration failed.";

        failures.push(`Student ID ${studentId}: ${detail}`);
      }
    }

    if (successful > 0) {
      setRegistrationMessage(
        `Successfully registered ${successful} student(s).`
      );
    }

    if (failed > 0) {
      setRegistrationError(
        `${failed} registration(s) could not be completed. ` +
          failures.slice(0, 5).join(" ")
      );
    }

    if (successful > 0 && typeof reload === "function") {
      await reload();
    }

    setSelectedStudentIds([]);
  } finally {
    setRegistering(false);
  }
};
  return (
    <div className="admin-page-full d-flex">
      <InstituteSidebar />

      <div className="admin-content">
        <InstituteTopbar />

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h2 className="h5 fw-bold mb-0">Exams</h2>

          <button
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            <i className="bi bi-download" /> Export CSV
          </button>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="admin-card p-3 p-md-4 mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h3 className="h6 fw-bold mb-0">Available Exams</h3>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={loadAvailableExams}
              disabled={availableLoading}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Refresh Exams
            </button>
          </div>

          {availableError && (
            <div className="alert alert-warning py-2 small">{availableError}</div>
          )}

          {availableLoading ? (
            <p className="text-muted small mb-0">Loading available exams...</p>
          ) : availableExams.length === 0 ? (
            <p className="text-muted small mb-0">
              No active exams are currently available.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle admin-table mb-0">
                <thead>
                  <tr>
                    <th>Exam</th>
                    <th>Program</th>
                    <th>Course</th>
                    <th>Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {availableExams.map((exam) => (
                    <tr key={exam.id}>
                      <td className="fw-semibold">{exam.name}</td>
                      <td>{exam.program || "—"}</td>
                      <td>{exam.course_name || "—"}</td>
                      <td>
                        {exam.fee != null
                          ? `₹${Number(exam.fee).toLocaleString("en-IN")}`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <hr className="my-4" />

          <h3 className="h6 fw-bold mb-3">Register a Student for an Exam</h3>

          {registrationMessage && (
            <div className="alert alert-success py-2 small">
              {registrationMessage}
            </div>
          )}

          {registrationError && (
            <div className="alert alert-danger py-2 small">
              {registrationError}
            </div>
          )}

          <form onSubmit={registerStudent}>
            <div className="row g-3 align-items-end">
              
<div className="col-12 col-md-5">
  <label className="form-label small fw-semibold">
    Select Students
  </label>

  <div className="border rounded p-3">
    <div className="form-check mb-2">
      <input
        id="select-all-students"
        type="checkbox"
        className="form-check-input"
        checked={
          studentOptions.length > 0 &&
          studentOptions.every((student) =>
            selectedStudentIds.includes(
              String(student.userId ?? student.user_id)
            )
          )
        }
        disabled={studentOptions.length === 0 || registering}
        onChange={(event) => {
          if (event.target.checked) {
            setSelectedStudentIds(
              studentOptions.map((student) =>
                String(student.userId ?? student.user_id)
              )
            );
          } else {
            setSelectedStudentIds([]);
          }
        }}
      />

      <label
        htmlFor="select-all-students"
        className="form-check-label fw-semibold"
      >
        Select All Students ({studentOptions.length})
      </label>
    </div>

    <hr className="my-2" />

    <div style={{ maxHeight: "180px", overflowY: "auto" }}>
      {studentOptions.map((student) => {
        const userId = String(student.userId ?? student.user_id);

        const name =
          student.fullName ||
          student.full_name ||
          student.name ||
          student.username ||
          student.email ||
          `Student ${userId}`;

        return (
          <div className="form-check mb-2" key={userId}>
            <input
              id={`student-${userId}`}
              type="checkbox"
              className="form-check-input"
              checked={selectedStudentIds.includes(userId)}
              disabled={registering}
              onChange={(event) => {
                setSelectedStudentIds((previous) =>
                  event.target.checked
                    ? [...new Set([...previous, userId])]
                    : previous.filter((id) => id !== userId)
                );
              }}
            />

            <label
              htmlFor={`student-${userId}`}
              className="form-check-label"
            >
              {name}
              {student.email ? ` (${student.email})` : ""}
            </label>
          </div>
        );
      })}
    </div>

    <div className="text-muted small mt-2">
      {selectedStudentIds.length} student(s) selected
    </div>
  </div>
</div>


              <div className="col-12 col-md-5">
                <label className="form-label small fw-semibold">Exam</label>
                <select
                  className="form-select"
                  value={selectedExamId}
                  onChange={(event) => setSelectedExamId(event.target.value)}
                  required
                >
                  <option value="">Select exam</option>
                  {availableExams.map((exam) => (
                    <option key={exam.id} value={exam.id}>
                      {exam.name} — {exam.program || "Exam"} — ₹
                      {Number(exam.fee || 0).toLocaleString("en-IN")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-2">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={registering || availableLoading || studentOptions.length === 0}
                >
                  {registering
  ? "Registering..."
  : selectedStudentIds.length > 1
    ? `Register ${selectedStudentIds.length} Students`
    : "Register"}
                </button>
              </div>
            </div>

            {selectedExam && (
              <p className="text-muted small mt-2 mb-0">
                Selected: {selectedExam.name}
                {selectedExam.course_name ? ` · ${selectedExam.course_name}` : ""}
              </p>
            )}

            {studentOptions.length === 0 && (
              <p className="text-muted small mt-2 mb-0">
                No students with usable user IDs are available. Refresh the student list or check the students API response.
              </p>
            )}
          </form>
        </div>

        <div className="row g-3 mb-3">
          {cards.map((card) => (
            <div className="col-6 col-md-4" key={card.label}>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">
                  <i className={`bi ${card.icon}`} />
                </div>
                <div className="admin-stat-value">{card.value}</div>
                <div className="admin-stat-label">{card.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-card p-3 mb-3">
          <div className="row g-2">
            <div className="col-12 col-md-8">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search" />
                </span>
                <input
                  className="form-control"
                  placeholder="Search by student, email or exam..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>

            <div className="col-12 col-md-4">
              <select
                className="form-select"
                value={courseFilter}
                onChange={(event) => setCourseFilter(event.target.value)}
              >
                <option value="All">All Courses</option>
                {courseNames.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="admin-card p-3 p-md-4">
          {loading && <p className="text-muted mb-0">Loading registrations...</p>}

          {!loading && filtered.length === 0 && (
            <p className="text-muted small mb-0">
              {rows.length === 0
                ? "None of your students has registered for an exam yet."
                : "No exams match your filters."}
            </p>
          )}

          {!loading && filtered.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Exam</th>
                      <th>Course</th>
                      <th>Registered Date</th>
                      <th>Fee</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paged.map((row, index) => (
                      <tr key={row.id}>
                        <td className="text-muted">
                          {(page - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td>
                          <div className="fw-semibold">{row.studentName}</div>
                          {row.email && (
                            <div className="text-muted small">{row.email}</div>
                          )}
                        </td>
                        <td>{row.examName}</td>
                        <td>{row.courseName}</td>
                        <td className="text-muted small">{fmtDate(row.registeredAt)}</td>
                        <td>
                          {row.fee != null
                            ? `₹${Number(row.fee).toLocaleString("en-IN")}`
                            : "—"}
                        </td>
                        <td>
                          <span className={`admin-status-pill ${statusClass(row.displayStatus)}`}>
                            {row.displayStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pager
                page={page}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
